/*
 * TTJ delivery checker (PDP) - serviceability via the Zip Code Availability API.
 *
 * The API call, validation, messages and cookie live in assets/ttj-delivery-core.js (window.TTJDelivery),
 * shared with the global pincode popup so there is one API integration.
 *
 *  - Input must be exactly 6 digits, otherwise a validation message is shown and no request is made.
 *  - available === true  -> success message (+ city/state when returned)
 *  - available === false -> "not available" message (amber, with the cross icon)
 *  - Any failure -> generic error message (never shown as "not available").
 *  - While a request is in flight the button shows a spinner and further submits are ignored.
 *  - A valid submitted pincode is saved in the session cookie; on every page load a saved pincode fills the
 *    input and is re-checked against the API for the current page (results are never carried between pages).
 *  - When the popup has just checked a pincode on this page it announces the result (ttj:delivery-result) and
 *    this element shows it without a second request.
 *
 * Delivery state (read by assets/ttj-delivery-gate.js to guard Add to Cart / Buy it now):
 *   element.deliveryState   'idle' | 'loading' | 'available' | 'unavailable' | 'error' | 'invalid'
 *   element.deliveryPincode the pincode the state refers to ('' for idle / invalid)
 *   event 'ttj:delivery-state' (bubbles) on every change.
 */
if (!customElements.get('ttj-delivery-checker')) {
  customElements.define(
    'ttj-delivery-checker',
    class TtjDeliveryChecker extends HTMLElement {
      get core() {
        return window.TTJDelivery;
      }

      connectedCallback() {
        this.form = this.querySelector('.ttj-delivery__form');
        this.input = this.querySelector('.ttj-delivery__input');
        this.result = this.querySelector('.ttj-delivery__result');
        if (!this.form || !this.input || !this.result) return;
        if (!this.core) {
          console.error('ttj-delivery-core.js is not loaded; the delivery checker is inactive.');
          return;
        }
        this.button = this.querySelector('.ttj-delivery__button');
        this.message = this.result.querySelector('.ttj-delivery__message');
        this.iconOk = this.result.querySelector('.ttj-delivery__icon--ok');
        this.iconError = this.result.querySelector('.ttj-delivery__icon--error');
        this.loading = false;
        this.requestId = 0;
        this.deliveryState = 'idle';
        this.deliveryPincode = '';

        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.check();
        });
        // Typing again clears a previous result so another pincode can be checked (and revalidated)
        this.input.addEventListener('input', () => {
          if (!this.loading) this.reset();
        });

        // The popup already checked this pincode on this page: show its result, no second API request
        this.onPopupResult = (event) => this.applyExternalResult(event.detail);
        document.addEventListener('ttj:delivery-result', this.onPopupResult);

        // Remembered pincode: fill the input and re-check it for THIS product on every page load
        this.checkSavedPincode();
        if (!this.pageShowBound) {
          this.pageShowBound = true;
          window.addEventListener('pageshow', (event) => {
            // Back/forward cache restore: re-sync in case the pincode was changed on another product page
            if (event.persisted) this.checkSavedPincode();
          });
        }
      }

      disconnectedCallback() {
        if (this.onPopupResult) document.removeEventListener('ttj:delivery-result', this.onPopupResult);
      }

      checkSavedPincode() {
        if (!this.isConnected || this.loading || this.closest('quick-add-modal')) return; // skip hidden quick-add copy
        const saved = this.core.getSavedPincode();
        if (!saved) return; // nothing saved: keep the existing initial behaviour
        this.input.value = saved;
        this.check(); // always a fresh API call for the current product page
      }

      applyExternalResult(detail) {
        if (!this.isConnected || this.loading || this.closest('quick-add-modal') || !detail) return;
        if (!this.core.isValidPincode(detail.pincode) || !detail.data) return;
        this.input.value = detail.pincode;
        this.input.setAttribute('aria-invalid', 'false');
        this.applyResult(detail.pincode, detail.data);
      }

      applyResult(pincode, data) {
        if (data.available === true) {
          this.setDeliveryState('available', pincode);
          this.show('success', this.core.formatAvailable(data));
        } else {
          this.setDeliveryState('unavailable', pincode);
          this.show('unavailable', this.core.MESSAGES.unavailable);
        }
      }

      setDeliveryState(state, pincode) {
        this.deliveryState = state;
        this.deliveryPincode = pincode || '';
        this.dataset.deliveryState = state;
        this.dispatchEvent(
          new CustomEvent('ttj:delivery-state', { bubbles: true, detail: { state, pincode: this.deliveryPincode } })
        );
      }

      setLoading(isLoading) {
        this.loading = isLoading;
        if (isLoading) {
          this.setAttribute('aria-busy', 'true');
        } else {
          this.removeAttribute('aria-busy');
        }
        if (this.button) this.button.disabled = isLoading;
        if (this.input) this.input.readOnly = isLoading; // keep the pincode visible but stable while checking
      }

      async check() {
        if (this.loading) return; // ignore repeated clicks / Enter while a request is running

        const core = this.core;
        const pincode = this.input.value.trim();
        if (!core.isValidPincode(pincode)) {
          this.setDeliveryState('invalid', '');
          this.show('error', this.dataset.invalidMessage || core.MESSAGES.invalid);
          this.input.setAttribute('aria-invalid', 'true');
          return;
        }

        this.input.setAttribute('aria-invalid', 'false');
        core.savePincode(pincode); // valid 6-digit pincode: remember it for other pages
        this.setDeliveryState('loading', pincode);
        this.setLoading(true);
        const requestId = ++this.requestId;
        try {
          const data = await core.fetchAvailability(pincode);
          if (requestId !== this.requestId) return;
          this.applyResult(pincode, data);
        } catch (error) {
          if (requestId !== this.requestId) return;
          this.setDeliveryState('error', pincode);
          this.show('error', core.MESSAGES.error);
        } finally {
          this.setLoading(false);
        }
      }

      // state: 'success' (green/purple), 'unavailable' (amber), 'error' (red)
      show(state, text) {
        this.message.textContent = text;
        this.result.classList.toggle('is-success', state === 'success');
        this.result.classList.toggle('is-unavailable', state === 'unavailable');
        this.result.classList.toggle('is-error', state === 'error');
        this.iconOk.hidden = state !== 'success';
        this.iconError.hidden = state !== 'error' && state !== 'unavailable'; // the cross icon is kept for both
        this.result.hidden = false;
      }

      reset() {
        this.input.setAttribute('aria-invalid', 'false');
        this.result.hidden = true;
        this.message.textContent = '';
        this.setDeliveryState('idle', '');
      }
    }
  );
}
